/*
 * Header search dropdown (header layout "logo-search-icons").
 * - Focus with empty field: recent searches, popular searches, popular products.
 * - Typing: live suggestions/collections/products from Shopify predictive search,
 *   rendered by sections/header-search-suggest.liquid.
 * - Keyboard: ↑/↓ to move, Enter to open, Esc to close.
 */
if (!customElements.get('header-search')) {
  const RECENT_KEY = 'bab_recent_searches';
  const RECENT_MAX = 5;
  const MIN_CHARS = 2;

  const readRecent = () => {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string') : [];
    } catch (e) {
      return [];
    }
  };
  const writeRecent = (list) => {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {}
  };

  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.hs-panel');
      this.clearBtn = this.querySelector('.hs-clear');
      this.recentGroup = this.querySelector('.hs-group--recent');
      this.recentList = this.querySelector('[data-hs-recent]');
      this.viewAll = this.querySelector('[data-hs-viewall]');
      this.cache = new Map();
      this.activeIndex = -1;
      this.debounceTimer = null;

      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.panel.addEventListener('click', (e) => this.onPanelClick(e));
      this.querySelector('[data-hs-clear-recent]')?.addEventListener('click', (e) => {
        e.preventDefault();
        writeRecent([]);
        this.renderRecent();
        this.input.focus();
      });

      this.onDocClick = (e) => {
        if (!this.contains(e.target)) this.close();
      };
      this.onDocKey = (e) => {
        if (e.key === 'Escape' && this.hasAttribute('open')) {
          this.close();
          this.input.blur();
        }
      };

      this.clearBtn.hidden = this.input.value.length === 0;
      this.renderRecent();
    }

    get query() {
      return this.input.value.trim();
    }

    open() {
      if (this.hasAttribute('open')) return;
      this.setAttribute('open', '');
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onDocKey);
      if (this.query.length >= MIN_CHARS && this.panel.dataset.state === 'empty') this.fetchResults();
      this.toggleVideos(true);
    }

    // Promo videos only load/play while the dropdown is open.
    toggleVideos(play) {
      this.panel.querySelectorAll('video[data-hs-video]').forEach((video) => {
        if (play && video.offsetParent !== null) {
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.setActive(-1);
      this.toggleVideos(false);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onDocKey);
    }

    setState(state) {
      this.panel.dataset.state = state;
      this.setActive(-1);
      if (this.hasAttribute('open')) this.toggleVideos(true);
    }

    onInput() {
      this.clearBtn.hidden = this.input.value.length === 0;
      this.open();
      clearTimeout(this.debounceTimer);
      if (this.query.length < MIN_CHARS) {
        this.controller?.abort();
        this.renderRecent();
        this.setState('empty');
        return;
      }
      this.debounceTimer = setTimeout(() => this.fetchResults(), 180);
    }

    buildUrl(q) {
      const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('section_id', 'header-search-suggest');
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', this.dataset.limit || '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
      return url.toString();
    }

    async fetchResults() {
      const q = this.query;
      const url = this.buildUrl(q);
      this.querySelectorAll('[data-hs-term]').forEach((el) => (el.textContent = q));
      this.viewAll.href = `${this.dataset.searchUrl || '/search'}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;

      if (this.cache.has(url)) {
        this.renderResults(this.cache.get(url));
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      if (this.panel.dataset.state !== 'results') this.setState('loading');
      else this.panel.dataset.state = 'loading';

      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        this.cache.set(url, html);
        if (q === this.query) this.renderResults(html);
      } catch (e) {
        if (e.name !== 'AbortError') this.setState('empty');
      }
    }

    renderResults(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fragment = doc.querySelector('[data-hs-fragment]');
      if (!fragment || Number(fragment.dataset.count) === 0) {
        this.renderRecent();
        this.setState('none');
        return;
      }
      ['queries', 'collections', 'products'].forEach((key) => {
        const slot = fragment.querySelector(`[data-hs-slot="${key}"]`);
        const target = this.querySelector(`[data-hs-target="${key}"]`);
        target.innerHTML = slot ? slot.innerHTML : '';
        const group = this.querySelector(`[data-hs-group="${key}"]`);
        group.hidden = target.children.length === 0;
      });
      // Hide the whole left column if it has nothing in it.
      const side = this.querySelector('.hs-results__side');
      side.hidden = [...side.querySelectorAll('[data-hs-group]')].every((g) => g.hidden);
      this.setState('results');
    }

    renderRecent() {
      const list = readRecent();
      this.recentGroup.hidden = list.length === 0;
      this.recentList.innerHTML = '';
      const searchUrl = this.dataset.searchUrl || '/search';
      list.forEach((term) => {
        const a = document.createElement('a');
        a.className = 'hs-chip hs-chip--recent';
        a.href = `${searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        a.dataset.hsItem = '';
        a.dataset.hsQuery = term;
        a.innerHTML =
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
        a.append(document.createTextNode(term));
        this.recentList.append(a);
      });
    }

    saveRecent(term) {
      term = (term || '').trim();
      if (term.length < MIN_CHARS) return;
      const list = readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      writeRecent(list.slice(0, RECENT_MAX));
    }

    track(term) {
      window.dispatchEvent(new CustomEvent('searchKeywordEntered', { detail: { keyword: term } }));
    }

    onSubmit(e) {
      const active = this.items()[this.activeIndex];
      if (active) {
        e.preventDefault();
        active.click();
        return;
      }
      if (this.query.length === 0) {
        e.preventDefault();
        this.input.focus();
        return;
      }
      this.saveRecent(this.query);
      this.track(this.query);
    }

    onPanelClick(e) {
      const item = e.target.closest('[data-hs-item]');
      if (!item) return;
      const term = item.dataset.hsQuery || this.query;
      this.saveRecent(term);
      this.track(term);
    }

    items() {
      return [...this.panel.querySelectorAll('[data-hs-item]')].filter((el) => el.offsetParent !== null);
    }

    setActive(index) {
      const items = this.items();
      items.forEach((el) => el.classList.remove('is-active'));
      this.activeIndex = index;
      const el = items[index];
      if (el) {
        el.classList.add('is-active');
        el.scrollIntoView({ block: 'nearest' });
        if (!el.id) el.id = `hs-item-${Math.random().toString(36).slice(2, 9)}`;
        this.input.setAttribute('aria-activedescendant', el.id);
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeydown(e) {
      if (!this.hasAttribute('open')) return;
      const count = this.items().length;
      if (!count) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.setActive((this.activeIndex + 1) % count);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.setActive(this.activeIndex <= 0 ? count - 1 : this.activeIndex - 1);
      } else if (e.key === 'Tab') {
        this.close();
      }
    }
  }

  customElements.define('header-search', HeaderSearch);
}
